import { _CO } from "../../../utils/constants";
import { updateNodeDataAndReturn } from "../sales-block/utils";
import {
  GROSS_PROFITS_RS_T,
  SALES_AGG_SECTION_T,
  SELLING_PRICE_INCREASE,
  SELLING_PRICE_RS_T,
} from "./AgGridTurnoverLrl";

/**
 * @desc Process rows with themes included in ARRAY_INCLUDE_TURNOVER_SELLING_PRICE
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - Updated rate value with selling price increase
 */
export const processRowsIncludeTurnoverSellingPrice = (
  data,
  api,
  node,
  columnIndex,
) => {
  const baseRate = Number(data?.rate || 0);
  const fiscalMonthIndex = _CO[columnIndex];

  let cumul = 0;

  // Find the selling price increase row in the same parent group
  const sellingPriceIncreaseRow = api
    .getRowNode(node?.id)
    ?.parent?.allLeafChildren?.find(
      (node) => node?.data?.subThemes === SELLING_PRICE_INCREASE,
    );

  if (sellingPriceIncreaseRow) {
    // Calculate cumulative selling price increase up to the current month
    for (let j = 1; j <= columnIndex; j++) {
      const monthKey = _CO[j];
      const monthImpact = Number(
        sellingPriceIncreaseRow?.data?.[monthKey] || 0,
      );
      cumul += monthImpact;
    }
  }

  const newValue = baseRate + cumul;

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, newValue);
};

/**
 * @desc Process rows with themes included in ARRAY_INCLUDE_TURNOVER_GROSS_PROFITS_RS_T
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 * @param {Object} grossProfitData - Gross profit data from api
 *
 * @returns {number} - Updated rate value with gross profits in Rs/T
 */
export const processRowsGrossProfitsRsT = (
  data,
  api,
  node,
  columnIndex,
  grossProfitData,
) => {
  const fiscalMonthIndex = _CO[columnIndex];
  const subTheme = data?.subThemes;
  const allRows = [];

  api.forEachNode((rowNode) => {
    if (rowNode?.data) {
      allRows.push(rowNode.data);
    }
  });

  const sellingPriceRow =
    allRows.find(
      (row) => row.theme === SELLING_PRICE_RS_T && row.subThemes === subTheme,
    ) || null;

  const sellingPriceValue = sellingPriceRow
    ? Number(sellingPriceRow[fiscalMonthIndex] || 0)
    : 0;

  const grossProfitsPValue =
    grossProfitData.find(
      (row) =>
        row.source.name === subTheme && row.fiscalMonth === fiscalMonthIndex,
    )?.amount || 0;

  const newValue = sellingPriceValue - grossProfitsPValue;

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, newValue);
};

/**
 * @desc Process rows with themes included in ARRAY_INCLUDE_TURNOVER_GROSS_PROFITS_P
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - Updated rate value with gross profits percentage
 */
export const processRowsGrossProfitsP = (data, api, node, columnIndex) => {
  const fiscalMonthIndex = _CO[columnIndex];
  const subTheme = data?.subThemes;

  const allRows = [];

  api.forEachNode((rowNode) => {
    if (rowNode?.data) {
      allRows.push(rowNode.data);
    }
  });

  const sellingPriceRow = allRows.find(
    (row) => row.theme === SELLING_PRICE_RS_T && row.subThemes === subTheme,
  );

  const grossProfitRow = allRows.find(
    (row) => row.theme === GROSS_PROFITS_RS_T && row.subThemes === subTheme,
  );

  const sellingPriceValue = Number(sellingPriceRow?.[fiscalMonthIndex] || 0);
  const grossProfitValue = Number(grossProfitRow?.[fiscalMonthIndex] || 0);

  let newValue = 0;

  if (sellingPriceValue !== 0) {
    newValue = Number(
      ((grossProfitValue / sellingPriceValue) * 100).toFixed(2),
    );
  }

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, newValue);
};

/**
 * @desc Process rows with themes included in ARRAY_INCLUDE_TURNOVER_TURNOVER_LRL_RS
 *
 * @param {Object} data - Row data
 * @param {Object} api - AG Grid API
 * @param {Object} node - Row node
 * @param {number} columnIndex - Month index (1-12)
 *
 * @returns {number} - Updated rate value with turnover LRL in Rs
 */
export const processRowsTurnoverLrlRs = (data, api, node, columnIndex) => {
  const fiscalMonthIndex = _CO[columnIndex];
  const subTheme = data?.subThemes;
  const allRows = [];

  api.forEachNode((rowNode) => {
    if (rowNode?.data) {
      allRows.push(rowNode.data);
    }
  });

  const salesAggRow =
    allRows.find(
      (row) => row.theme === SALES_AGG_SECTION_T && row.subThemes === subTheme,
    ) || 0;

  const sellingPriceRow =
    allRows.find(
      (row) => row.theme === SELLING_PRICE_RS_T && row.subThemes === subTheme,
    ) || 0;

  const tonsValue = Number(salesAggRow?.[fiscalMonthIndex] || 0);
  const priceValue = Number(sellingPriceRow?.[fiscalMonthIndex] || 0);

  const newValue = tonsValue * priceValue;

  return updateNodeDataAndReturn(api, node?.id, fiscalMonthIndex, newValue);
};
